import { useCallback, useEffect, useRef, useState } from 'react';
import { svcGet } from '../lib/api';

// Fix 7 G3: how many services the signed-in member still has to answer (the
// latest GET /mine `pending`), for the Team Comms Services dot. Reads /mine
// only while `enabled` (the Services tab is visible and the church is in simple
// mode, Fix 7c F6): on Team Comms mount, on window focus, when `notifStamp`
// changes (a service notification arrived) and when the Services view closes.
// Never on a timer. While the Services view is open (`paused`) the view's own
// /mine loads report through `setPendingCount` instead, so one event never
// costs two /mine calls; that report also lands after the view has closed.
export function useServicesPending(userId, enabled, notifStamp = '', paused = false) {
  const [pendingCount, setPendingCount] = useState(0);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  // Fix 7c F10: the newest answer wins. A load that started before the view's
  // own report (an answer still in flight when the view closed) is dropped.
  const seq = useRef(0);
  const report = useCallback((n) => { seq.current += 1; setPendingCount(n); }, []);

  const load = useCallback(async () => {
    if (!userId || !enabled || pausedRef.current) return;
    const mine = ++seq.current;
    try {
      const data = await svcGet('/mine', userId);
      if (mine === seq.current) setPendingCount((data?.pending || []).length);
    } catch { /* keep the last answer */ }
  }, [userId, enabled]);

  useEffect(() => { load(); }, [load]);

  // Fix 7c F10: when the Services view closes, read /mine once: an answer whose
  // reload landed after the view unmounted never reached setPendingCount.
  const wasPaused = useRef(paused);
  useEffect(() => {
    if (wasPaused.current && !paused) load();
    wasPaused.current = paused;
  }, [paused, load]);

  const firstStamp = useRef(notifStamp);
  useEffect(() => {
    if (notifStamp === firstStamp.current) return;
    firstStamp.current = notifStamp;
    load();
  }, [notifStamp, load]);

  useEffect(() => {
    if (!enabled) return undefined;
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [enabled, load]);

  return { pendingCount, setPendingCount: report };
}

export default useServicesPending;
